import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Search, ArrowUpRight } from 'lucide-react';
import TransitionLink from '../components/layout/TransitionLink';
import { BlogPost, defaultAuthor, formatDate, getReadingTimeText } from '../data/blog';
import SEO from '../components/seo/SEO';
import Footer from '../components/layout/Footer';
import OpPageHeader from '../components/optimus/OpPageHeader';

interface BlogPageProps {
  darkMode: boolean;
}

const blogPostKeys = [
  'productionPortfolioDollar',
  'reactPerformanceOptimization',
  'webSecurityImplementation',
  'aiIntegrationGuide',
  'noteoriousAppDevelopment',
  'firebaseCloudSetup',
] as const;

const slugMap: Record<(typeof blogPostKeys)[number], string> = {
  productionPortfolioDollar: 'production-portfolio-dollar',
  reactPerformanceOptimization: 'react-performance-optimization',
  webSecurityImplementation: 'web-security-implementation',
  aiIntegrationGuide: 'ai-integration-guide',
  noteoriousAppDevelopment: 'noteorious-app-development',
  firebaseCloudSetup: 'firebase-cloud-setup',
};

// op.blog.* short keys, so list categories match the article pages' translations
const categoryKeyMap: Record<(typeof blogPostKeys)[number], string> = {
  productionPortfolioDollar: 'dollar',
  reactPerformanceOptimization: 'perf',
  webSecurityImplementation: 'sec',
  aiIntegrationGuide: 'ai',
  noteoriousAppDevelopment: 'note',
  firebaseCloudSetup: 'fire',
};

/**
 * Blog in the Optimus design system — an editorial, numbered article list
 * (like the template's features section) with category filter + search.
 */
const BlogPage: React.FC<BlogPageProps> = ({ darkMode }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const blogPosts: BlogPost[] = useMemo(
    () =>
      blogPostKeys.map((key) => ({
        slug: slugMap[key],
        title: t(`blogPosts.${key}.title`),
        description: t(`blogPosts.${key}.description`),
        content: '',
        author: defaultAuthor,
        publishedAt: t(`blogPosts.${key}.publishedAt`),
        tags: (t(`blogPosts.${key}.tags`, { returnObjects: true }) as string[]) || [],
        category: t(`op.blog.${categoryKeyMap[key]}.category`),
        readingTime: parseInt(t(`blogPosts.${key}.readingTime`)) || 10,
        wordCount: 2800,
        featured: false,
        seoKeywords: [],
      })),
    [t]
  );

  const categories = ['All', ...Array.from(new Set(blogPosts.map((post) => post.category)))];

  const filteredPosts = useMemo(() => {
    let filtered = [...blogPosts].sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(q) ||
          post.description.toLowerCase().includes(q) ||
          post.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }
    if (selectedCategory !== 'All') {
      filtered = filtered.filter((post) => post.category === selectedCategory);
    }
    return filtered;
  }, [blogPosts, searchTerm, selectedCategory]);

  return (
    <main className="op-page noise-overlay relative min-h-screen overflow-x-hidden">
      <SEO
        title={`${t('blog.title')} | Talal Alkhaled - Computer Engineering Insights`}
        description={t('blog.description')}
        url="https://talkhaled.com/blog"
        pageType="blog"
      />

      <OpPageHeader
        label={t('op.sub.blogLabel')}
        line1={t('op.sub.blogH1')}
        line2={t('op.sub.blogH2')}
        description={t('op.sub.blogDesc')}
      />

      {/* Controls */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-y border-foreground/10 py-5">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm font-mono transition-colors duration-300 border ${
                  selectedCategory === category
                    ? 'bg-foreground text-background border-foreground'
                    : 'border-foreground/15 text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                }`}
              >
                {category === 'All' ? t('blog.all') : category}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-72">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('blog.searchPlaceholder')}
              className="w-full bg-transparent border border-foreground/15 ps-9 pe-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground/60 focus:border-foreground outline-none transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Article list */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        {filteredPosts.length > 0 ? (
          <div>
            {filteredPosts.map((post, index) => (
              <TransitionLink
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group block border-b border-foreground/10"
              >
                <article className="flex flex-col lg:flex-row gap-4 lg:gap-16 py-10 lg:py-14">
                  <div className="shrink-0 lg:w-12">
                    <span className="font-mono text-sm text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 font-mono text-xs text-muted-foreground">
                      <span className="uppercase tracking-widest">{post.category}</span>
                      <span className="text-foreground/20">|</span>
                      <span dir="ltr">{formatDate(post.publishedAt, i18n.language)}</span>
                      <span className="text-foreground/20">|</span>
                      <span>{getReadingTimeText(post.readingTime, i18n.language)}</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-display mb-3 group-hover:translate-x-2 transition-transform duration-500">
                      {post.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed max-w-3xl line-clamp-2">
                      {post.description}
                    </p>
                  </div>

                  <div className="shrink-0 flex lg:items-center">
                    <span className="inline-flex items-center gap-2 text-sm text-foreground">
                      {t('blog.readMore')}
                      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </article>
              </TransitionLink>
            ))}
          </div>
        ) : (
          <div className="border border-foreground/10 py-24 text-center">
            <p className="font-mono text-sm text-muted-foreground mb-6">{t('blog.noArticlesFound')}</p>
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All');
              }}
              className="px-6 py-3 bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
            >
              {t('blog.clearFilters')}
            </button>
          </div>
        )}
      </div>

      <Footer darkMode={darkMode} />
    </main>
  );
};

export default BlogPage;
